import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import type { MetricIdentity } from "@/metrics/catalog"
import { usePersonasList } from "@/persona"
import { useTestSetsList } from "@/test-set/catalog"
import { useOrganization } from "@clerk/nextjs"
import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  type SortDescriptor,
} from "@heroui/react"
import clsx from "clsx"

import { KEYBOARD_ACTIVE_ROW_CLASSES } from "@/lib/keyboardNavigationStyles"
import { useActiveItemAutoScroll } from "@/hooks/useActiveItemAutoScroll"
import { useCollectionPageShortcuts } from "@/hooks/useCollectionPageShortcuts"
import { useQueryParams } from "@/hooks/useQueryParams"

import { GROUP_BY_TYPE, type GroupByType } from "../types/chart-types"
import type { FilterType, WidgetFilterConfig } from "../types/core"
import { getMetadataGroupDisplayName } from "../utils/chart/splitKeys"
import type { MetricValue } from "../utils/chartShared"
import { DASHBOARD_WIDGET_SCROLLBAR_STYLE } from "../utils/scrollbarStyles"
import {
  buildTableColumns,
  buildTableRows,
  formatMetricCellValue,
  getTableCellSortValue,
  resolveTableExportState,
  resolveTableMetricIds,
  type TableColumnDefinition,
  type TableRowData,
} from "../utils/tableWidgetData"
import {
  getDefaultTableSortColumn,
  resolveTableSortColumn,
} from "../utils/tableWidgetSorting"
import type { MetricWidgetConfig, WidgetExportData } from "./BaseWidget"
import { ChartWidgetSkeleton } from "./ChartWidgetSkeleton"

interface TableWidgetTableProps {
  data: {
    dateRange: { start: Date; end: Date }
    error: string | null
    isGlobalOverrideActive: boolean
    isLoading: boolean
    metricRecords?: Record<string, MetricIdentity>
    metricValues: MetricValue[]
  }
  config: MetricWidgetConfig &
    WidgetFilterConfig & {
      type: "table" | "chart"
      groupBy?: GroupByType
      groupByMetadataKey?: string
      filters?: FilterType
    }
  onExportDataReady?: (data: WidgetExportData | null) => void
}

export type { TableRowData } from "../utils/tableWidgetData"

const METRIC_BACKGROUND_EXCLUDED_COLUMN_KEYS = new Set([
  "test_set_id",
  "run_agent_id",
  "persona_id",
  "run_template_name",
  "mutation_id",
  "count",
])

const getMetricCellBackgroundColor = ({
  columnKey,
  value,
}: {
  columnKey: string
  value: number | undefined
}): string | undefined => {
  if (METRIC_BACKGROUND_EXCLUDED_COLUMN_KEYS.has(columnKey) || value == null) {
    return undefined
  }

  const intensity = Math.abs(value - 0.5) * 2

  return value >= 0.5
    ? `hsl(var(--heroui-success) / ${intensity})`
    : `hsl(var(--heroui-danger) / ${intensity})`
}

export const TableWidgetTable: React.FC<TableWidgetTableProps> = ({
  data,
  config,
  onExportDataReady,
}) => {
  const { organization } = useOrganization()
  const containerRef = useRef<HTMLDivElement>(null)
  const { getHref } = useQueryParams()

  const metricIds = useMemo(
    () =>
      resolveTableMetricIds({
        filters: config.filters,
        metricId: config.metricId,
        metricIds: config.metricIds,
      }),
    [config.filters, config.metricId, config.metricIds],
  )
  const showPerMetricDenominators = metricIds.length > 1
  const defaultSortColumn = getDefaultTableSortColumn({
    groupBy: config.groupBy,
    groupByMetadataKey: config.groupByMetadataKey,
    metricIds,
  })

  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: defaultSortColumn,
    direction: "ascending",
  })

  const selectedTestSetIds = useMemo(
    () => config.filters?.testSetIds || [],
    [config.filters?.testSetIds],
  )

  const { data: testSetList } = useTestSetsList({
    enabled: selectedTestSetIds.length > 0,
  })

  const { data: personaList } = usePersonasList({
    enabled: config.groupBy === GROUP_BY_TYPE.PERSONA,
  })

  const testSetNameMap = useMemo(() => {
    const nameMap = new Map<string, string>()

    if (testSetList) {
      testSetList.forEach((testSet) => {
        nameMap.set(testSet.id, testSet.display_name)
      })
    }

    return nameMap
  }, [testSetList])

  const personaNameMap = useMemo(() => {
    const nameMap = new Map<string, string>()

    if (personaList) {
      personaList.forEach((persona: { id: string; display_name: string }) => {
        nameMap.set(persona.id, persona.display_name)
      })
    }

    return nameMap
  }, [personaList])

  const tableRows = useMemo(
    () =>
      buildTableRows({
        groupBy: config.groupBy,
        groupByMetadataKey: config.groupByMetadataKey,
        metricIds,
        metricValues: data.metricValues,
        personaNameMap,
        testSetNameMap,
      }),
    [
      config.groupBy,
      config.groupByMetadataKey,
      data.metricValues,
      metricIds,
      personaNameMap,
      testSetNameMap,
    ],
  )

  const columns = useMemo<TableColumnDefinition[]>(
    () =>
      buildTableColumns({
        groupBy: config.groupBy,
        groupByMetadataKey: config.groupByMetadataKey,
        metricIds,
        metricRecords: data.metricRecords ?? null,
      }),
    [config.groupBy, config.groupByMetadataKey, data.metricRecords, metricIds],
  )

  const handleSortChange = useCallback((descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor)
  }, [])

  const activeSortDescriptor = useMemo<SortDescriptor>(() => {
    const column = resolveTableSortColumn({
      columns,
      fallbackColumn: defaultSortColumn,
      requestedColumn: sortDescriptor.column,
    })

    return column === sortDescriptor.column
      ? sortDescriptor
      : { column, direction: "ascending" }
  }, [columns, defaultSortColumn, sortDescriptor])

  const sortedRows = useMemo(() => {
    return [...tableRows].sort((a, b) => {
      const columnKey = activeSortDescriptor.column as string
      const aValue = getTableCellSortValue(a, columnKey)
      const bValue = getTableCellSortValue(b, columnKey)

      if (aValue == null && bValue == null) {
        return 0
      }
      if (aValue == null) {
        return 1
      }
      if (bValue == null) {
        return -1
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return activeSortDescriptor.direction === "ascending"
          ? aValue - bValue
          : bValue - aValue
      }

      const comparison = String(aValue).localeCompare(
        String(bValue),
        undefined,
        {
          numeric: true,
          sensitivity: "base",
        },
      )

      return activeSortDescriptor.direction === "ascending"
        ? comparison
        : -comparison
    })
  }, [activeSortDescriptor, tableRows])

  const handleRowClick = useCallback(
    (row: TableRowData): void => {
      if (
        !organization?.slug ||
        !config.groupBy ||
        config.groupBy === GROUP_BY_TYPE.TEMPLATE ||
        config.groupBy === GROUP_BY_TYPE.MUTATION
      ) {
        return
      }

      const groupByToField: Record<string, string> = {
        test_set: "test_set_id",
        agent: "run_agent_id",
        persona: "persona_id",
      }

      const groupByToUrl: Record<string, string> = {
        test_set: "test-sets/:id",
        agent: "agents/:id",
        persona: "personas?id=:id",
      }

      const urlKey = groupByToUrl[config.groupBy as keyof typeof groupByToUrl]
      const fieldKey =
        groupByToField[config.groupBy as keyof typeof groupByToField]

      if (!urlKey || !fieldKey) {
        return
      }

      const rawValue = (row as unknown as Record<string, unknown>)[fieldKey]
      if (typeof rawValue !== "string" || rawValue.length === 0) {
        return
      }

      const entityPath = `/${urlKey.replace(":id", rawValue)}`
      const canonicalPath = getHref(entityPath, organization.slug)
      window.open(`${window.location.origin}${canonicalPath}`, "_blank")
    },
    [config.groupBy, getHref, organization?.slug],
  )

  const renderCell = useCallback(
    (column: { key: string }, row: TableRowData) => {
      const dimensionRenderers: Record<
        string,
        (tableRow: TableRowData) => string
      > = {
        test_set_id: (tableRow) =>
          String(tableRow.testSetName || tableRow.test_set_id || "-"),
        run_agent_id: (tableRow) =>
          String(tableRow.agentName || tableRow.run_agent_id || "-"),
        mutation_id: (tableRow) =>
          String(tableRow.mutationName || tableRow.mutation_id || "-"),
        persona_id: (tableRow) =>
          String(tableRow.personaName || tableRow.persona_id || "-"),
        run_template_name: (tableRow) =>
          String(tableRow.templateName || tableRow.run_template_name || "-"),
        assignee: (tableRow) => String(tableRow.assignee || "Unassigned"),
        metadataValue: (tableRow) =>
          tableRow.metadataValue
            ? getMetadataGroupDisplayName(tableRow.metadataValue)
            : "-",
      }

      const dimensionRenderer = dimensionRenderers[column.key]
      if (dimensionRenderer) {
        return dimensionRenderer(row)
      }

      if (column.key === "count") {
        return String(row.count ?? 0)
      }

      const formattedMetricValue = formatMetricCellValue({
        cell: row.metricCells[column.key],
        showDenominator: showPerMetricDenominators,
      })

      if (formattedMetricValue == null) {
        return <span className="text-coval-text-muted">-</span>
      }

      return <span className="text-center">{formattedMetricValue}</span>
    },
    [showPerMetricDenominators],
  )

  const isRowNavigable = Boolean(
    config.groupBy &&
    config.groupBy !== GROUP_BY_TYPE.ASSIGNEE &&
    config.groupBy !== GROUP_BY_TYPE.TEMPLATE &&
    config.groupBy !== GROUP_BY_TYPE.MUTATION,
  )

  const getRowId = useCallback(
    (row: TableRowData) =>
      String(
        row.test_set_id ||
          row.run_agent_id ||
          row.persona_id ||
          row.run_template_name ||
          row.mutation_id ||
          row.assignee ||
          row.metadataValue ||
          "aggregated",
      ),
    [],
  )

  const {
    handlePointerEnter,
    handlePointerLeave,
    isActiveItem,
    keyboardActiveItemId,
  } = useCollectionPageShortcuts({
    enabled: isRowNavigable,
    getItemId: getRowId,
    itemLabel: "row",
    items: isRowNavigable ? sortedRows : [],
    onOpenItem: handleRowClick,
  })

  useActiveItemAutoScroll({
    activeItemId: keyboardActiveItemId,
    selectorAttribute: "data-widget-row-id",
  })

  const prevExportDataRef = useRef("")
  useEffect(() => {
    if (!onExportDataReady || data.isLoading) {
      return
    }

    const { exportDataKey, tableData } = resolveTableExportState({
      columns,
      dateRange: data.dateRange,
      error: data.error,
      rows: sortedRows,
    })
    if (prevExportDataRef.current === exportDataKey) {
      return
    }

    prevExportDataRef.current = exportDataKey

    if (tableData) {
      onExportDataReady({
        tableData,
        dateRange: data.dateRange,
        widgetType: "table",
      })
      return
    }

    onExportDataReady(null)
  }, [
    columns,
    data.dateRange,
    data.error,
    data.isLoading,
    onExportDataReady,
    sortedRows,
  ])

  if (data.isLoading) {
    return <ChartWidgetSkeleton />
  }

  if (data.error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-coval-text-muted">
          <div className="text-sm">Failed to load data</div>
        </div>
      </div>
    )
  }

  if (tableRows.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-coval-text-muted">
          <div className="text-sm">No data available</div>
          <div className="text-xs mt-1">
            Select metrics and test sets to display data
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className="w-full h-full overflow-auto"
      style={DASHBOARD_WIDGET_SCROLLBAR_STYLE}
    >
      <Table
        isHeaderSticky
        aria-label="Table"
        sortDescriptor={activeSortDescriptor}
        onSortChange={handleSortChange}
        removeWrapper
      >
        <TableHeader>
          {columns.map((column) => (
            <TableColumn
              className="capitalize"
              key={column.key}
              allowsSorting={column.sortable}
            >
              {column.label.replace("_", " ")}
            </TableColumn>
          ))}
        </TableHeader>
        <TableBody items={sortedRows} emptyContent="No data available">
          {(row: TableRowData) => (
            <TableRow
              className={clsx(
                config.groupBy &&
                  config.groupBy !== GROUP_BY_TYPE.TEMPLATE &&
                  "cursor-pointer hover:bg-surface-1",
                isActiveItem(getRowId(row)) && KEYBOARD_ACTIVE_ROW_CLASSES,
              )}
              data-widget-row-id={getRowId(row)}
              onClick={() => handleRowClick(row)}
              onMouseEnter={() => handlePointerEnter(getRowId(row))}
              onMouseLeave={() => handlePointerLeave(getRowId(row))}
              key={getRowId(row)}
            >
              {columns.map((column) => {
                const cellValue = row.metricCells[column.key]?.value
                return (
                  <TableCell
                    key={column.key}
                    className="text-nowrap"
                    style={{
                      backgroundColor: getMetricCellBackgroundColor({
                        columnKey: column.key,
                        value: cellValue,
                      }),
                    }}
                  >
                    {renderCell(column, row)}
                  </TableCell>
                )
              })}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
